// @flow

// AppSync 구독은 끊긴 이유와 상관없이 다시 붙인다 (#201).
//
// 예전 핸들러들은 에러 메시지가 정확히 "Connection closed"일 때만 1초 뒤 한 번 재구독했다.
// 그런데 @aws-amplify/pubsub 4.x의 AWSAppSyncRealTimeProvider는 상황마다 다른 메시지를 보낸다:
// 절전 중 소켓이 소리 없이 죽으면 "Timeout disconnect"(keepalive 5분 뒤), 재구독 시점에 아직
// 오프라인이면 "Connection failed: …". 그래서 노트북이 깨어난 직후 1초 재구독이 실패하면 그 뒤로
// History·송출 자막이 새로고침 전까지 멈췄다.
//
// MQTT(#145)와 달리 이 provider는 연결 실패 시 소켓 상태를 CLOSED로 되돌리므로, 실패한 연결이
// 캐시에 남지 않는다. 다시 구독만 하면 새 소켓으로 붙는다.
//
// 다만 Wi‑Fi가 꺼지거나 노트북이 잠들어도 Chrome은 열린 WebSocket을 닫지 않아, 라이브러리는
// keepalive(5분)가 끝나야 끊김을 안다. 그래서 브라우저가 offline을 알리거나 절전에서 깨어난 것이
// 보이면 공유 연결을 직접 끊어, 모든 구독이 바로 재구독 흐름에 들어가게 한다.

import { API, PubSub } from "aws-amplify";
import * as Sentry from "@sentry/react";

// 재구독 간격: 처음엔 빠르게, 오래 끊기면 15초마다
export const RESUBSCRIBE_DELAYS_MS = [1000, 2000, 5000, 10000, 15000];
// 이만큼 버틴 구독이 끊기면 새 끊김으로 보고 간격을 처음부터 다시 센다
const HEALTHY_AFTER_MS = 60 * 1000;
const REPORT_THROTTLE_MS = 60 * 1000;
// 이 간격으로 시계를 보다가, 이보다 오래 멈췄다 깨어나면 절전 복귀로 본다.
// 백그라운드 탭은 타이머가 1분 단위로 묶이므로 그보다 넉넉히 잡는다
const WAKE_CHECK_MS = 10 * 1000;
const WAKE_GAP_MS = 2 * 60 * 1000;
// @aws-amplify/pubsub 4.x AWSAppSyncRealTimeProvider 내부 값
const CONNECTION_CLOSED = "Connection closed";
const WEBSOCKET_OPEN = 1;

let graphqlApi: any = API;
let getRealtimeProvider: () => any = () => PubSub.awsAppSyncRealTimeProvider;

// 테스트 전용
export function setAppSyncDepsForTest(deps: {
  api: any,
  realtimeProvider?: () => any,
}) {
  graphqlApi = deps.api;
  if (deps.realtimeProvider) {
    getRealtimeProvider = deps.realtimeProvider;
  }
}

// 앱의 모든 AppSync 구독이 함께 쓰는 WebSocket을 끊는다. 각 구독은 "Connection closed"를 받는다.
// 라이브러리 내부(_errorDisconnect)를 쓰므로 @aws-amplify/pubsub를 올리면 테스트로 다시 확인한다.
export function dropRealtimeConnection() {
  const provider = getRealtimeProvider();
  const socket = provider?.awsRealTimeSocket;
  if (
    socket == null ||
    socket.readyState !== WEBSOCKET_OPEN ||
    typeof provider._errorDisconnect !== "function"
  ) {
    return;
  }
  // 죽은 소켓의 close 이벤트가 뒤늦게 와서 새로 붙은 연결까지 끊지 않도록 핸들러를 먼저 뗀다
  socket.onclose = null;
  socket.onerror = null;
  socket.onmessage = null;
  clearTimeout(provider.keepAliveTimeoutId);
  provider._errorDisconnect(CONNECTION_CLOSED);
}

let connectionWatchInstalled = false;
function installConnectionWatch() {
  if (connectionWatchInstalled) {
    return;
  }
  connectionWatchInstalled = true;
  window.addEventListener("offline", dropRealtimeConnection);
  let lastTick = Date.now();
  setInterval(() => {
    const now = Date.now();
    if (now - lastTick > WAKE_GAP_MS) {
      console.warn(
        `[AppSyncSubscription] timers paused ${Math.round((now - lastTick) / 1000)}s (sleep?), reconnecting`
      );
      dropRealtimeConnection();
    }
    lastTick = now;
  }, WAKE_CHECK_MS);
}

// 연결 상태 표시등(#204)용: 지금 재연결을 기다리는 구독 수
const retrying: Set<{}> = new Set();
const retryListeners: Set<() => void> = new Set();
function setRetrying(token: {}, on: boolean) {
  if (retrying.has(token) === on) {
    return;
  }
  if (on) {
    retrying.add(token);
  } else {
    retrying.delete(token);
  }
  retryListeners.forEach((fn) => fn());
}
export function getAppSyncRetryingCount(): number {
  return retrying.size;
}
export function onAppSyncRetryingChange(fn: () => void): () => void {
  retryListeners.add(fn);
  return () => {
    retryListeners.delete(fn);
  };
}

const lastReported: Map<string, number> = new Map();
function report(context: string, message: string, extra: Object) {
  // 오프라인이면 어차피 전송되지 않는다
  if (navigator.onLine === false) {
    return;
  }
  const now = Date.now();
  if (now - (lastReported.get(context) ?? 0) < REPORT_THROTTLE_MS) {
    return;
  }
  lastReported.set(context, now);
  Sentry.captureException(new Error(`${context}: ${message}`), { extra });
}

function errorMessage(err: any): string {
  return (
    err?.error?.errors?.[0]?.message ??
    err?.errors?.[0]?.message ??
    String(err?.message ?? err ?? "unknown error")
  );
}

// 재구독 뒤 콜백(다시 조회 등)이 실패해도 구독 흐름이나 화면을 깨지 않게 한다
function runSafely(context: string, fn: () => mixed) {
  const warn = (e) =>
    console.warn(`[${context}] refresh after resubscribe failed`, e);
  try {
    const result: any = fn();
    if (result != null && typeof result.catch === "function") {
      result.catch(warn);
    }
  } catch (e) {
    warn(e);
  }
}

// 끊기면 알아서 다시 구독하는 AppSync 구독. 반환값은 기존 구독처럼 unsubscribe()를 가진다.
// next: 기존 obs.subscribe의 next와 같은 인자({ value })를 받는다
// onResubscribed: 재구독할 때마다 불린다 (예: 놓친 메시지 조회, 설정 다시 조회)
export function subscribeAppSync(
  operation: { query: string, variables?: Object },
  handlers: {
    next: (event: any) => mixed,
    onResubscribed?: () => mixed,
    context: string,
  }
): { unsubscribe: () => void } {
  const token = {};
  let stopped = false;
  let attempt = 0;
  let startedAt = 0;
  let current = null;
  let timer = null;

  installConnectionWatch();

  const start = () => {
    if (stopped) {
      return;
    }
    startedAt = Date.now();
    current = graphqlApi.graphql(operation).subscribe({
      next: handlers.next,
      error: (err) => {
        if (stopped) {
          return;
        }
        setRetrying(token, true);
        if (Date.now() - startedAt >= HEALTHY_AFTER_MS) {
          attempt = 0;
        }
        const delay =
          RESUBSCRIBE_DELAYS_MS[Math.min(attempt, RESUBSCRIBE_DELAYS_MS.length - 1)];
        attempt += 1;
        const message = errorMessage(err);
        console.warn(
          `[${handlers.context}] AppSync subscription lost (${message}), retry in ${delay}ms`
        );
        report(handlers.context, `AppSync subscription lost: ${message}`, {
          attempt,
          rawError: err,
        });
        timer = setTimeout(resubscribe, delay);
      },
    });
  };

  const resubscribe = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (stopped) {
      return;
    }
    // 오프라인이면 붙을 수 없으니 online 이벤트를 기다린다 (혹시 놓쳐도 15초 뒤 다시 본다).
    // 오프라인에서 재구독·다시 조회를 하면 실패만 쌓이고, 실패한 조회가 화면 상태를 망가뜨릴 수 있다
    if (navigator.onLine === false) {
      timer = setTimeout(
        resubscribe,
        RESUBSCRIBE_DELAYS_MS[RESUBSCRIBE_DELAYS_MS.length - 1]
      );
      return;
    }
    try {
      current?.unsubscribe();
    } catch (e) {
      // 이미 에러로 끝난 구독이라 무시
    }
    setRetrying(token, false);
    start();
    if (!stopped && handlers.onResubscribed) {
      runSafely(handlers.context, handlers.onResubscribed);
    }
  };

  // 네트워크가 돌아왔다고 브라우저가 알려주면 남은 대기 시간을 건너뛴다
  const onOnline = () => {
    if (timer) {
      resubscribe();
    }
  };
  window.addEventListener("online", onOnline);

  start();

  return {
    unsubscribe: () => {
      stopped = true;
      setRetrying(token, false);
      window.removeEventListener("online", onOnline);
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      try {
        current?.unsubscribe();
      } catch (e) {
        // 끊긴 구독의 정리 경로에서 라이브러리가 던질 수 있다 — 무시
      }
    },
  };
}
